import { useFormState, useWatch } from "react-hook-form"
import ExpanderPanel from "@rio-cloud/rio-uikit/ExpanderPanel"
import { FormattedMessage } from "react-intl"
import { FormTextInput } from "@/components/Form/FormTextInput"
import { FormSelect } from "@/components/Form/FormSelect"
import Icon from "@rio-cloud/rio-uikit/Icon"
import { VolumeInput } from "./VolumeInput"
import { TransportationReport } from "@/model/transportation-report/transportation-report"

export type ShipmentsFormInput = Pick<TransportationReport, "shipments">

export const ShipmentPanel = (props: { shipmentIndex: number; removeShipment: (() => void) | undefined }) => {
    const { removeShipment, shipmentIndex } = props
    const formState = useFormState({ name: `shipments.${shipmentIndex}` })
    const hasErrors = formState.errors.shipments !== undefined && formState.errors.shipments[shipmentIndex] !== undefined
    const shipmentType = useWatch({ name: `shipments.${shipmentIndex}.type` })

    return <ExpanderPanel
        open
        title={<>
            {hasErrors ? <span className={"uikit-icon icon-error-sign text-color-danger padding-right-5"}/> : undefined}
            <FormattedMessage id={"shipment.numbered"} values={{ num: shipmentIndex + 1 }}/>
            {removeShipment !== undefined && <span className={"padding-left-10"}>
                <span
                    className={"uikit-icon icon-trash text-color-gray cursor-pointer"}
                    data-testid={`remove-shipment-${shipmentIndex}`}
                    onClick={event => {
                        event.stopPropagation() // Stop expander from collapsing
                        removeShipment()
                    }}/>
            </span>}
        </>}
        iconLeft={true}
        bsStyle={"separator"}
        titleClassName={hasErrors ? "text-danger" : ""}
        unmountOnExit={false}
    >
        <div className={"bg-lightest padding-20"}>
            <div className={"display-flex gap-20 margin-bottom-15"}>
                <div className={"flex-basis-100pct"}>
                    <FormSelect<ShipmentsFormInput>
                        fieldName={`shipments.${shipmentIndex}.type`}
                        label={<FormattedMessage id={"shipment.type"}/>}
                        required
                        options={[{
                            id: "MATERIALS",
                            label: <>
                                <Icon size='h3' iconName="icon-cube" />
                                <FormattedMessage id={`shipment.type.materials`}/>
                            </>
                        },{
                            id: "EMPTIES",
                            label: <>
                                <Icon size='h3' iconName="icon-empty" />
                                <FormattedMessage id={`shipment.type.empties`}/>
                            </>
                        }]}
                        validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipment.type.required"}/> }}
                    />
                </div>
                <div className={"flex-basis-100pct"}>
                    <FormTextInput<ShipmentsFormInput>
                        fieldName={`shipments.${shipmentIndex}.shipmentNumber`}
                        label={<FormattedMessage id={"shipment.shipmentNumber"}/>}
                        required={true}
                        validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipmentNumber.required"}/> }}
                    />
                </div>
            </div>
            <div className={"display-flex gap-20 margin-bottom-15"}>
                <div className={"flex-basis-100pct"}>
                    <FormTextInput<ShipmentsFormInput>
                        fieldName={`shipments.${shipmentIndex}.shipFrom.duns`}
                        label={<FormattedMessage id={"shipFrom.duns"}/>}
                        required={true}
                        mask={"000000000"}
                        minLength={9}
                        maxLength={9}
                        validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipFrom.duns.required"}/> }}
                    />
                </div>
                <div className={"flex-basis-100pct"}>
                    <FormTextInput<ShipmentsFormInput>
                        fieldName={`shipments.${shipmentIndex}.shipFrom.supplierCode`}
                        label={<FormattedMessage id={"shipFrom.supplierCode"}/>}
                        required={true}
                        validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipFrom.supplierCode.required"}/> }}
                    />
                </div>
            </div>
            <div className={"display-flex gap-20 margin-bottom-15"}>
                <div className={"flex-basis-100pct"}>
                    <FormTextInput<ShipmentsFormInput>
                        fieldName={`shipments.${shipmentIndex}.shipTo.duns`}
                        label={<FormattedMessage id={"shipTo.duns"}/>}
                        required={true}
                        mask={"000000000"}
                        minLength={9}
                        maxLength={9}
                        validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipTo.duns.required"}/> }}
                    />
                </div>
                {shipmentType === "MATERIALS" && (
                    <div className={"flex-basis-100pct"}>
                        <FormTextInput<ShipmentsFormInput>
                            fieldName={`shipments.${shipmentIndex}.shipTo.plantCode`}
                            label={<><FormattedMessage id={"shipTo.plantCode"}/> <b><FormattedMessage id={"shipTo.plantCode.infoMsg"}/></b></>}
                            required={true}
                            validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipTo.plantCode.required"}/> }}
                        />
                    </div>
                )}
                {shipmentType === "EMPTIES" && (
                    <div className={"flex-basis-100pct"}>
                        <FormTextInput<ShipmentsFormInput>
                            fieldName={`shipments.${shipmentIndex}.shipTo.supplierCode`}
                            label={<><FormattedMessage id={"shipTo.supplierCode"}/> <b><FormattedMessage id={"shipTo.supplierCode.infoMsg"}/></b></>}
                            required={true}
                            validationMessageOverrides={{ required: <FormattedMessage id={"freightPaymentDetails.shipTo.supplierCode.required"}/> }}
                        />
                    </div>
                )}
                {!shipmentType && <div className={"flex-basis-100pct"} />}
            </div>
            <VolumeInput shipmentIndex={shipmentIndex} />
        </div>
    </ExpanderPanel>
}
